// Предохранитель от шторма событий в Hawk — общий для клиента и сервера.
//
// Одна поломка на горячем пути (упавший стрим репетитора, циклический ретрай
// офлайн-очереди, отвал внешнего API в Inngest) даёт сотни одинаковых событий
// в минуту. Без ограничителя это выжигает квоту проекта и превращает ленту в
// один бесконечный дубль, за которым не видно остальных ошибок.
//
// Две независимые защиты: дедуп по отпечатку в окне и общий потолок в минуту.

export interface HawkThrottleOptions {
	/** Максимум отправок в минуту (все ошибки суммарно). */
	maxPerMinute: number;
	/** Окно подавления повторов одного отпечатка, мс. */
	dedupeWindowMs: number;
}

/** Значения по умолчанию — одни для всех поверхностей StudyHub. */
export const HAWK_THROTTLE_DEFAULTS: HawkThrottleOptions = {
	maxPerMinute: 60,
	dedupeWindowMs: 60_000,
};

/**
 * Создаёт фильтр: `true` — событие можно отправить, `false` — гасим.
 * Состояние живёт в замыкании, поэтому у каждой поверхности оно своё.
 */
export function createHawkThrottle(
	options: HawkThrottleOptions = HAWK_THROTTLE_DEFAULTS,
): (fingerprint: string, now?: number) => boolean {
	const seenAt = new Map<string, number>();
	let windowStartedAt = 0;
	let sentInWindow = 0;

	return function allow(fingerprint: string, now = Date.now()): boolean {
		if (now - windowStartedAt >= 60_000) {
			windowStartedAt = now;
			sentInWindow = 0;
			// Заодно подчищаем протухшие отпечатки, чтобы Map не рос вечно.
			for (const [key, at] of seenAt) {
				if (now - at > options.dedupeWindowMs) seenAt.delete(key);
			}
		}

		if (sentInWindow >= options.maxPerMinute) return false;

		const previous = seenAt.get(fingerprint);
		if (previous !== undefined && now - previous < options.dedupeWindowMs) return false;

		seenAt.set(fingerprint, now);
		sentInWindow += 1;
		return true;
	};
}

/**
 * Отпечаток ошибки: имя + сообщение + первый кадр стека. Первый кадр отделяет
 * «одна и та же ошибка» от «одинаковый текст из разных мест» — иначе generic-
 * сообщения вроде `fetch failed` схлопывались бы в одно.
 */
export function hawkFingerprint(error: Error): string {
	const firstFrame = error.stack?.split("\n")[1]?.trim() ?? "";
	return `${error.name}|${error.message}|${firstFrame}`;
}
